import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlarmClock, Sparkles, X, LayoutList } from 'lucide-react';
import { CommentItem, MomentItem } from './CommentItem';
import { TaskCommentEditor } from './comment-editor/TaskCommentEditor.jsx';
import { TaskCreateWizard } from './comment-editor/TaskCreateWizard.jsx';

// slice 02.1 · paso b.5d.2 — presentacional. El costado derecho del panel de detalle: tabs
// (info?/comentarios/team/docs/meets) + la feed (MomentItem/CommentItem) + la lista de team +
// los estados vacíos de docs/meets + la caja de escribir un comentario nuevo. Era JSX inline de
// `ComponentTaskHoy` (`task-panel-comments`).
//
// `editingCommentId`/`editDraftContent`/`expandedCommentEditor` se quedan container-side (no
// 100% leaf-local como sugería el sketch original del sub-plan): el modal de edición expandida
// (`TaskCommentEditorModal`, portal a `document.body`) vive fuera de este árbol y necesita leer
// el mismo estado -- mismo criterio que `activeCalendlyPopoverTaskId` en b.5a/§6 (compartido
// entre dos puntos de render, no puede ser leaf-local). Toda mutación/toast queda en el
// contenedor (boundary lint): `onToggleMediaVisibility`/`onSubmitComment`/`onSaveEdit`/etc. son
// las únicas escrituras.
export const TaskCommentsPanel = ({
    isTagsCollapsed,
    activeTab,
    onTabChange,
    listRef,
    infoContent,
    comments = [],
    currentUserId,
    editingCommentId,
    expandedCommentEditor,
    editDraftContent,
    onEditContentChange,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onExpandEditEditor,
    onToggleReaction,
    onDeleteComment,
    onToggleMediaVisibility,
    onExpandImage,
    hoveredReminderId,
    hoveredReminderData,
    reminderTooltipPos,
    onReminderMouseEnter,
    onReminderMouseLeave,
    onReminderClick,
    activeReminderCommentId,
    onOpenReminderPopover,
    onCloseReminderPopover,
    renderReminderPopover,
    canTagUsers,
    usersToSelectFrom = [],
    userId,
    editorTranslate,
    taskSmartTags = [],
    onSmartTagsChange,
    pendingReminder,
    canComment,
    createDraftContent,
    createResetKey,
    onCreateContentChange,
    onSubmitComment,
    onExpandCreateEditor,
    showReminderPopover,
    onCloseCommentReminderPopover,
    // Integración main→dev, epic D1.4 — modo comentario-sugerencia (morado) del cuadro de trabajo.
    suggestionMode = false,
    onExitSuggestionMode,
    onEnterSuggestionMode,
    taskMode = false,
    onExitTaskMode,
    onEnterTaskMode,
    onMentionSelect,
    activeTask,
    user,
}) => {
    const { t: translate } = useTranslation('tasks');
    const tabs = isTagsCollapsed ? ['info', 'comentarios', 'team', 'docs', 'meets'] : ['comentarios', 'team', 'docs', 'meets'];
    const usersForTag = canTagUsers ? usersToSelectFrom : [];

    return (
        <div className="task-panel-comments">
            <div className="comments-tabs-header">
                {tabs.map(tab => (
                    <span
                        key={tab}
                        className={`comments-tab-btn ${activeTab === tab ? 'active' : ''}`}
                        onClick={() => onTabChange(tab)}
                    >
                        {translate(`comments.tabs.${tab}`)}
                    </span>
                ))}
            </div>

            <div className="comments-list-container" ref={listRef}>
                {activeTab === 'info' ? (
                    <div style={{ padding: 'var(--space-4)', overflowY: 'auto', height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {infoContent}
                    </div>
                ) : activeTab === 'team' ? (
                    <div className="team-members-list">
                        {comments.map(m => (
                            <div key={m.id} className="team-member-item">
                                <img src={m.avatar} alt={m.name} className="team-member-avatar" loading="lazy" />
                                <span className="team-member-name">{m.name}</span>
                            </div>
                        ))}
                        {comments.length === 0 && (
                            <div style={{ color: '#666', fontStyle: 'italic', padding: 'var(--space-3) 0', fontSize: '11px' }}>
                                {translate('comments.noTeamMembers')}
                            </div>
                        )}
                    </div>
                ) : (
                    (!comments || comments.length === 0) ? (
                        <div className="empty-state-container" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <div style={{ color: '#666', fontStyle: 'italic', padding: 'var(--space-3) 0', fontSize: '11px' }}>
                                {activeTab === 'docs' ? translate('comments.noDocs') :
                                 activeTab === 'meets' ? translate('comments.noMeets') :
                                 translate('comments.startConversation')}
                            </div>
                        </div>
                    ) : (
                        <div className="comments-feed">
                            {comments.map((c) => {
                                if (!c) return null;
                                const commentKey = c.comment_id;

                                if (c.comment_kind === 'moment') {
                                    return <MomentItem key={commentKey} comment={c} />;
                                }

                                const isEditing = editingCommentId === c.comment_id;
                                return (
                                    <CommentItem
                                        key={commentKey}
                                        comment={c}
                                        currentUserId={currentUserId}
                                        usersToSelectFrom={usersToSelectFrom}
                                        isEditing={isEditing && expandedCommentEditor !== 'edit'}
                                        renderCommentEditor={() => (
                                            <TaskCommentEditor
                                                mode="edit"
                                                content={editDraftContent || c.content}
                                                resetKey={c.comment_id}
                                                onContentChange={onEditContentChange}
                                                onSave={(html) => onSaveEdit(c.comment_id, html)}
                                                onCancel={onCancelEdit}
                                                users={usersForTag}
                                                userId={userId}
                                                translate={editorTranslate}
                                                autoFocus
                                                expandable
                                                onExpand={onExpandEditEditor}
                                            />
                                        )}
                                        onToggleReaction={onToggleReaction}
                                        onStartEdit={onStartEdit}
                                        onDeleteComment={onDeleteComment}
                                        onToggleMediaVisibility={onToggleMediaVisibility}
                                        onExpandImage={onExpandImage}
                                        hoveredReminderId={hoveredReminderId}
                                        hoveredReminderData={hoveredReminderData}
                                        reminderTooltipPos={reminderTooltipPos}
                                        onReminderMouseEnter={onReminderMouseEnter}
                                        onReminderMouseLeave={onReminderMouseLeave}
                                        onReminderClick={onReminderClick}
                                        activeReminderCommentId={activeReminderCommentId}
                                        onOpenReminderPopover={onOpenReminderPopover}
                                        onCloseReminderPopover={onCloseReminderPopover}
                                        renderReminderPopover={renderReminderPopover}
                                    />
                                );
                            })}
                        </div>
                    )
                )}
            </div>

            {activeTab === 'comentarios' && (
                <div className={`comment-write-container ${suggestionMode ? 'is-suggestion-mode' : ''}`}>
                    {suggestionMode && (
                        <div className="comment-suggestion-mode-banner">
                            <div className="comment-suggestion-mode-info">
                                <Sparkles size={12} color="#c084fc" />
                                <span>{translate('comments.suggestionModeBanner')}</span>
                            </div>
                            <button
                                type="button"
                                className="comment-suggestion-mode-close"
                                onClick={onExitSuggestionMode}
                                title={translate('comments.suggestionModeExit')}
                            >
                                <X size={12} />
                            </button>
                        </div>
                    )}
                    {taskMode && (
                        <div className="comment-task-mode-banner">
                            <div className="comment-task-mode-info">
                                <LayoutList size={12} color="#3b82f6" />
                                <span>Modo Tarea Activo</span>
                            </div>
                            <button
                                type="button"
                                className="comment-task-mode-close"
                                onClick={onExitTaskMode}
                                title="Salir del modo tarea"
                            >
                                <X size={12} />
                            </button>
                        </div>
                    )}

                    {taskSmartTags.length > 0 && (
                        <div className="comment-smart-tags-row">
                            {taskSmartTags.map(tg => (
                                <span key={tg} className="comment-smart-tag-badge">
                                    {tg}
                                </span>
                            ))}
                        </div>
                    )}

                    {pendingReminder && (
                        <div className="comment-smart-tags-row">
                            <span className="comment-smart-tag-badge pending-reminder-badge" style={{ backgroundColor: 'hsla(150, 85%, 70%, 0.3)', color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <AlarmClock size={12} />
                                {translate('comments.reminderSavedBadge')}
                            </span>
                        </div>
                    )}

                    {canComment && expandedCommentEditor !== 'create' && (
                        taskMode ? (
                            <TaskCreateWizard
                                activeTask={activeTask}
                                systemUsers={usersForTag}
                                user={user}
                                onFinish={onExitTaskMode}
                            />
                        ) : (
                            <TaskCommentEditor
                                mode="create"
                                content={createDraftContent}
                                resetKey={createResetKey}
                                autoFocus={suggestionMode}
                                onContentChange={onCreateContentChange}
                                onSubmit={onSubmitComment}
                                onSmartTagsChange={onSmartTagsChange}
                                onMentionSelect={onMentionSelect}
                                users={usersForTag}
                                userId={userId}
                                placeholder={suggestionMode ? translate('comments.suggestionPlaceholder') : translate('comments.writePlaceholder')}
                                translate={editorTranslate}
                                onEnterTaskMode={onEnterTaskMode}
                                onEnterSuggestionMode={onEnterSuggestionMode}
                                expandable
                                onExpand={onExpandCreateEditor}
                            />
                        )
                    )}

                    {showReminderPopover && renderReminderPopover(onCloseCommentReminderPopover)}
                </div>
            )}
        </div>
    );
};

export default TaskCommentsPanel;
